import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import { Typography, Button, Chip, InfoItem, Select, SimpleTable } from '../../design-system';
import {
  DashboardFormCards,
  DashboardFormCard,
  DashboardHeader as DashboardHeaderBase,
} from '../dashboard';
import { Filters } from './Filters';
import { momentTextFormat, formatContainerType } from '../../utils';
import {
  ContainerIsLoadedToChipLabels,
  ContainerIsLoadedToChipColors,
  CURRENCIES,
  currencyOptions,
  ContainerConditionToChipLabels,
  ContainerConditionToChipColors,
  ContainerStatusToChipLabels,
  ContainerStatusToChipColors,
} from '../../const';
import { User, ReportsGetContainersResponse, CurrencyCode } from '../../types';
import { getSavedFilters } from '../../hooks';
import { i18n } from '../../i18n';

interface ReportsPageProps {
  innerScroll?: boolean;
}

export const ReportsPage = styled.div<ReportsPageProps>`
  display: grid;
  grid-template-rows: max-content max-content 100%;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

const ReportsPageBody = styled.div`
  display: grid;
  grid-template-rows: 100%;
  padding-bottom: var(--spacing-10);
`;

const DashboardHeader = styled(DashboardHeaderBase)`
  grid-template-columns: 1fr minmax(15rem, max-content);
`;

interface ReportsHeaderProps {
  totalCount: number;
  currencyCode: CurrencyCode;
  handleExportExcelClick: () => void;
  handleShowReportsClick: () => void;
  handleCurrencyChange: (e: any) => void;
}

export const ReportsHeader = ({
  totalCount,
  currencyCode,
  handleExportExcelClick,
  handleShowReportsClick,
  handleCurrencyChange,
}: ReportsHeaderProps) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.reports.title', { totalCount })}
      </Typography>
      <Select options={currencyOptions} value={currencyCode} onChange={handleCurrencyChange} />
      <Button size={'medium'} onClick={handleExportExcelClick} disabled={!totalCount}>
        {i18n.t('button.exportExcel')}
      </Button>
      <Button id={'show-reports-button'} size={'medium'} onClick={handleShowReportsClick}>
        {i18n.t('button.showReports')}
      </Button>
    </DashboardHeader>
  );
};

export const ReportsPageInfos = ({ children }: any) => {
  return <DashboardFormCards id={'reports-cards'}>{children}</DashboardFormCards>;
};

interface ReportsGeneralInfoProps {
  user: User;
  startDate?: Date;
  endDate?: Date;
}

export const ReportsGeneralInfo = ({ user, startDate, endDate }: ReportsGeneralInfoProps) => {
  const filters = getSavedFilters('belongs-to');

  return (
    <DashboardFormCard paperSize={'full-size'}>
      <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
        {i18n.t('common.generalInformation')}
      </Typography>
      <InfoItem label={i18n.t('common.client')} value={filters.clientName} />
      <InfoItem label={i18n.t('label.country')} value={filters.country} />
      <InfoItem label={i18n.t('label.city')} value={filters.city} />
      <InfoItem label={i18n.t('common.yard')} value={filters.yardName} />
      <InfoItem
        label={i18n.t('label.from')}
        value={startDate && momentTextFormat(startDate, user)}
      />
      <InfoItem label={i18n.t('label.to')} value={endDate && momentTextFormat(endDate, user)} />
    </DashboardFormCard>
  );
};

export const ReportsPriceInfo = ({
  data,
  currencyCode,
}: {
  data: ReportsGetContainersResponse['data'];
  currencyCode: CurrencyCode;
}) => {
  const storageFee = data.reduce((result, container) => result + container.daysInTerminalPrice, 0);
  const craneFee = data.reduce(
    (result, container) => result + container.gateInCranePrice + container.gateOutCranePrice,
    0,
  );
  const allFee = storageFee + craneFee;

  return (
    <DashboardFormCard paperSize={'full-size'}>
      <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
        {i18n.t('pages.reports.priceInformation')}
      </Typography>
      <InfoItem
        label={i18n.t('label.storageFee')}
        value={`${storageFee.toFixed(2)}${CURRENCIES[currencyCode].symbol}`}
      />
      <InfoItem
        label={i18n.t('label.craneFee')}
        value={`${craneFee.toFixed(2)}${CURRENCIES[currencyCode].symbol}`}
      />
      <InfoItem
        label={i18n.t('label.allFee')}
        value={`${allFee.toFixed(2)}${CURRENCIES[currencyCode].symbol}`}
      />
    </DashboardFormCard>
  );
};

interface ReportsBodyProps {
  user: User;
  data: ReportsGetContainersResponse['data'];
  currencyCode: CurrencyCode;
  isSubmitClicked: boolean;
  states: { [key: string]: any };
  setStates: (key: string, value?: any, isEmptyFilter?: boolean) => void;
}

export const ReportsBody = ({
  user,
  data,
  currencyCode,
  isSubmitClicked,
  states,
  setStates,
}: ReportsBodyProps) => {
  const symbol = CURRENCIES[currencyCode].symbol;
  const tableData = {
    data: data,
    totalCount: data.length,
  };

  return (
    <ReportsPageBody>
      <SimpleTable
        data={tableData}
        filters={() => (
          <Filters
            user={user}
            isSubmitClicked={isSubmitClicked}
            setStates={setStates}
            {...states}
          />
        )}
        tableRowProps={(container, i) => ({
          key: 'storage-pricing' + i,
          onClick: () => {},
        })}
        columns={[
          {
            headText: () => i18n.t('label.containerNumber'),
            body: (container) => (
              <TableCell component={'th'} align={'left'}>
                {container.number}
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.type'),
            bodyText: (container) => formatContainerType(container.type),
          },
          {
            headText: () => i18n.t('label.condition'),
            body: (container) => (
              <TableCell align={'left'}>
                {container.condition && (
                  <Chip
                    size={'small'}
                    label={ContainerConditionToChipLabels[container.condition]}
                    color={ContainerConditionToChipColors[container.condition]}
                  />
                )}
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.status'),
            body: (container) => (
              <TableCell align={'left'}>
                <Chip
                  size={'small'}
                  label={ContainerStatusToChipLabels[container.status]}
                  color={ContainerStatusToChipColors[container.status]}
                />
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.gateInDateFrom'),
            bodyText: (container) => momentTextFormat(container.gateIn.completedAt, user),
          },
          {
            headText: () => i18n.t('label.gateOutDateTo'),
            bodyText: (container) =>
              container.gateOut?.completedAt
                ? momentTextFormat(container.gateOut?.completedAt, user)
                : '',
          },
          {
            headText: () => i18n.t('label.daysInTerminal'),
            bodyText: (container) => container.daysInTerminal + ' Days',
          },
          {
            headText: () => i18n.t('label.payingDays'),
            bodyText: (container) => container.payingDays + ' Days',
          },
          {
            headText: () => i18n.t('label.isLoaded'),
            body: (container) => (
              <TableCell align={'left'}>
                <Chip
                  size={'small'}
                  label={ContainerIsLoadedToChipLabels[container.isLoadedText]}
                  color={ContainerIsLoadedToChipColors[container.isLoadedText]}
                />
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.storageFee'),
            bodyText: (container) => container.daysInTerminalPrice + symbol,
          },
          {
            headText: () => i18n.t('label.craneInFee'),
            bodyText: (container) => `${container.gateInCranePrice} ${symbol}`,
          },
          {
            headText: () => i18n.t('label.craneOutFee'),
            bodyText: (container) => `${container.gateOutCranePrice} ${symbol}`,
          },
        ]}
      />
    </ReportsPageBody>
  );
};
